import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useEvent } from '../../../data/hooks/eventHooks';
import { useExhibitorsFilter, useExhibitorsList, useExhibitorsRecommendedFilter } from '../../../data/hooks/exhibitor/exhibitorHooks';
import { useExhibitorsTagCategories } from '../../../data/hooks/exhibitor/useExhibitorsTagCategories';
import { useI18n } from '../../../data/hooks/i18nHooks';
import Blocklink from '../../elements/Blocklink/Blocklink';
import Icon from '../../elements/icons/Icon';
import ListTitle from '../../elements/ListTitle';
import MaximizeToolbarIcon from '../../elements/MaximizeToolbarIcon';
import Scrollable from '../../elements/Scrollable';
import TopBarEvent from '../../elements/TopBar/TopBarEvent';
import DirectoryListItem from '../EventDirectory/elements/DirectoryListItem/DirectoryListItem';
import Screen from '../Screen';
import ExhibitorsFilterSelection from './elements/ExhibitorsFilterSelection';
import ExhibitorsSearchBar from './elements/ExhibitorsSearchBar';
import FilterScreen from './FilterScreen';

const APPLIED_RECOMMENDED_FILTER = {};

const EventExhibitors = (props) => {
    const {} = props,
        { eventId } = useParams(),
        event = useEvent(eventId),
        i18n = useI18n(),
        [showFilterScreen, setShowFilterScreen] = useState(false),
        recommendedFilter = useExhibitorsRecommendedFilter(event.id),
        tags = useExhibitorsTagCategories(event.id),
        currentFilter = useExhibitorsFilter(event.id),
        disableFilterButton = !tags.length,
        exhibitors = useExhibitorsList(event.id, {
            filter: currentFilter,
        });

    // Categories
    const categories = useMemo(() => {
        let categories = _(exhibitors)
            .sortBy('position')
            .map((item) => item.categories)
            .flatten()
            .uniq()
            .map((cat) => ({
                title: cat,
                exhibitors: exhibitors.filter((item) => item.categories.includes(cat)),
            }))
            .value();

        // Add uncategorized exhibitors
        categories.push({
            title: event.settings.exhibitors_label || i18n['label.exhibitors'],
            exhibitors: _(exhibitors)
                .filter((item) => item.is_exhibitor)
                .filter((item) => !item.categories || !item.categories.length)
                .value(),
        });

        // Sponsors are a separated category
        if (event.repeat_sponsors_on_exhibitors_list) {
            if (_(exhibitors).find((i) => i.is_sponsor)) {
                categories = [
                    {
                        title: event.settings.sponsors_label || i18n['label.sponsors'],
                        exhibitors: _(exhibitors)
                            .filter((i) => i.is_sponsor)
                            .value(),
                    },
                    ...categories,
                ];
            }
        }

        // Cleanup categories with no exhibitors
        categories = _(categories)
            .filter((cat) => cat.exhibitors.length)
            .value();

        return categories;
    }, [event, i18n, exhibitors]);

    // apply recommended filter initially
    useEffect(() => {
        if (!APPLIED_RECOMMENDED_FILTER[event.id]) {
            app.store.dispatch(app.actions.exhibitors.applyRecommendedFilter(event.id));
            APPLIED_RECOMMENDED_FILTER[event.id] = true;
        }
    }, [event.id]);

    // Callbacks
    const handleOpenMap = useCallback(() => app.store.dispatch(app.actions.ui.sidePanel.maximize(true)), []);
    const handleOpenFilterScreen = useCallback(() => setShowFilterScreen(true), []);
    const handleCloseFilterScreen = useCallback(() => setShowFilterScreen(false), []);

    // Show filter screen
    if (showFilterScreen) {
        return <FilterScreen exhibitors={exhibitors} filterParams={currentFilter} tags={tags} i18n={i18n} event={event} onClose={handleCloseFilterScreen} recommendedFilter={recommendedFilter} />;
    }

    // Show default screen
    let mapIcon = <Icon src="/img/event_map.png" color="white" />,
        header = (
            <div>
                <TopBarEvent event={event} icons={event.exhibitors_map_url ? [<MaximizeToolbarIcon icon={mapIcon} key="maximize-toolbar" />] : []}>
                    <span>{event.settings.exhibitors_label || ''}</span>
                </TopBarEvent>
                <hr />
                <ExhibitorsSearchBar event={event} filter={currentFilter} i18n={i18n} onOpenFilterScreen={handleOpenFilterScreen} disableFilterButton={disableFilterButton}>
                    <ExhibitorsFilterSelection event={event} tags={tags} selectedTags={currentFilter.tags} />
                </ExhibitorsSearchBar>
                <hr />
            </div>
        ),
        footer;

    // Map button on footer
    if (event.exhibitors_map_url) {
        footer = (
            <Blocklink customColor={event.settings.primary_color} onClick={handleOpenMap} next="next" className="marginTop">
                {i18n['label.map']}
            </Blocklink>
        );
    }

    return (
        <Screen header={header} footer={footer}>
            <Scrollable>
                {categories.map((category, i) => (
                    <div className="marginBottom" key={category.title}>
                        <ListTitle
                            style={{
                                textTransform: 'capitalize',
                            }}>
                            {category.title}
                        </ListTitle>
                        <ul className="list">
                            {category.exhibitors.map((item) => (
                                <DirectoryListItem event={event} item={item} key={item.id} />
                            ))}
                        </ul>
                    </div>
                ))}
            </Scrollable>
        </Screen>
    );
};

export default EventExhibitors;
